import React, { useState, useEffect } from "react";
import { Modal, Input, Alert, Spin, message } from "antd";
import axios from "axios";
import { API_DOMAIN } from "../constants";
import { fetchBillingDetailsQuickReply, injectBillingDetailsTemplate, useQv4Theme } from "./helpers";

// Raised right after a task's payment completes in full — the customer's
// profile is missing their real/legal name and/or an address, both required
// before an SO/PO/invoice or Shipday shipment can be raised. Sends the
// billing-details quick reply and, on success, hands off to the caller so it
// can open the chat + customer profile drawers for immediate follow-up.
export function BillingDetailsModal({ open, queryPk, queryId, customerName, customerPhone, userId, onClose, onSuccess }) {
  const [messageText, setMessageText] = useState("");
  const [templateLoading, setTemplateLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const { t } = useQv4Theme();

  useEffect(() => {
    if (!open) return;
    setTemplateLoading(true);
    fetchBillingDetailsQuickReply()
      .then(template => {
        if (!template) { setMessageText(""); return; }
        setMessageText(injectBillingDetailsTemplate(template.text, { customerName }));
      })
      .finally(() => setTemplateLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSend = async () => {
    if (!messageText.trim()) { message.warning("Message can't be empty"); return; }
    if (!customerPhone) { message.error("No phone number on file for this customer"); return; }
    setSending(true);
    try {
      const res = await axios.post(
        `${API_DOMAIN}/queries/${queryPk}/request-billing-details`,
        { userId, message_text: messageText.trim() },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (!res.data?.success) throw new Error(res.data?.message);
      message.success("Customer notified");
      onSuccess?.();
    } catch (e) {
      message.error(e?.response?.data?.message || e.message || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      open={open}
      title={`Billing Details Needed${queryId ? ` — #${queryId}` : ""}`}
      onCancel={onClose}
      onOk={handleSend}
      confirmLoading={sending}
      okText="Send to Customer"
      cancelText="Not now"
      destroyOnHidden
      width={520}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
        <Alert
          type="warning"
          showIcon
          message={`${customerName || "This customer"}'s profile is missing a real/legal name and/or an address.`}
          description="Both are required to raise the Sales Order, Purchase Order, invoice, and Shipday shipment for this task."
        />
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: t.textSub }}>
            Message <span style={{ color: "#ff4d4f" }}>*</span>
          </div>
          {templateLoading ? (
            <div style={{ textAlign: "center", padding: "20px 0" }}><Spin /></div>
          ) : (
            <Input.TextArea
              rows={10}
              value={messageText}
              onChange={e => setMessageText(e.target.value)}
              placeholder="Message to send"
            />
          )}
        </div>
      </div>
    </Modal>
  );
}
